import {
	Activity,
	BarChart3,
	Calendar,
	DollarSign,
	Download,
	ExternalLink,
	Eye,
	Filter,
	Info,
	MousePointer,
	Package,
	Search,
	Shield,
	Star,
	Target,
	TrendingUp,
	Users,
	X,
} from "lucide-react";
import React, { useCallback, useMemo, useState } from "react";
import { relevanceFromDistance } from "../utils";
import CreatePackageModal from "./createpackageModal";
import { Pagination } from "./Pagination";
import TargetingModal from "./targetingModal";

// Chevron down icon for dropdown (matching audience results)
const ChevronDownIcon = () => (
	<svg
		width="16"
		height="16"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
	>
		<polyline points="6,9 12,15 18,9"></polyline>
	</svg>
);

// Simple Export Dropdown Component
const PackageExportDropdown = ({ isExporting, onExport }) => {
	const [isOpen, setIsOpen] = useState(false);

	const ChevronDownIcon = () => (
		<svg
			width="16"
			height="16"
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
		>
			<polyline points="6,9 12,15 18,9"></polyline>
		</svg>
	);

	return (
		<div className="export-dropdown" style={{ position: "relative" }}>
			<button
				onClick={() => setIsOpen(!isOpen)}
				className="btn-medium-secondary"
				disabled={isExporting}
				style={{
					display: "flex",
					alignItems: "center",
					gap: "8px",
				}}
			>
				<Download size={16} />
				{isExporting ? "Exporting..." : "Export Results"}
				<ChevronDownIcon />
			</button>

			{isOpen && (
				<>
					<div
						className="dropdown-menu"
						style={{
							position: "absolute",
							top: "100%",
							right: 0,
							backgroundColor: "white",
							border: "1px solid #dee2e6",
							borderRadius: "4px",
							boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
							zIndex: 1000,
							minWidth: "200px",
							marginTop: "4px",
						}}
					>
						<button
							onClick={() => {
								onExport("summarized");
								setIsOpen(false);
							}}
							className="dropdown-item"
							style={{
								width: "100%",
								padding: "10px 16px",
								border: "none",
								background: "none",
								textAlign: "left",
								cursor: "pointer",
								fontSize: "14px",
								color: "#212529",
								transition: "background-color 0.2s",
								fontWeight: "500",
							}}
							onMouseEnter={(e) => (e.target.style.backgroundColor = "#f8f9fa")}
							onMouseLeave={(e) =>
								(e.target.style.backgroundColor = "transparent")
							}
						>
							Export Summarized
						</button>

						<button
							onClick={() => {
								onExport("detailed");
								setIsOpen(false);
							}}
							className="dropdown-item"
							style={{
								width: "100%",
								padding: "10px 16px",
								border: "none",
								background: "none",
								textAlign: "left",
								cursor: "pointer",
								fontSize: "14px",
								color: "#212529",
								transition: "background-color 0.2s",
								fontWeight: "500",
							}}
							onMouseEnter={(e) => (e.target.style.backgroundColor = "#f8f9fa")}
							onMouseLeave={(e) =>
								(e.target.style.backgroundColor = "transparent")
							}
						>
							Export Detailed
						</button>
					</div>

					{/* Click outside to close */}
					<div
						style={{
							position: "fixed",
							top: 0,
							left: 0,
							right: 0,
							bottom: 0,
							zIndex: 999,
						}}
						onClick={() => setIsOpen(false)}
					/>
				</>
			)}
		</div>
	);
};

// View Toggle Icon Component
const ViewToggleIcon = ({ isTiled }) => (
	<svg
		width="16"
		height="16"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
	>
		{isTiled ? (
			// Table view icon
			<>
				<line x1="8" y1="6" x2="21" y2="6"></line>
				<line x1="8" y1="12" x2="21" y2="12"></line>
				<line x1="8" y1="18" x2="21" y2="18"></line>
				<line x1="3" y1="6" x2="3.01" y2="6"></line>
				<line x1="3" y1="12" x2="3.01" y2="12"></line>
				<line x1="3" y1="18" x2="3.01" y2="18"></line>
			</>
		) : (
			// Grid view icon
			<>
				<rect x="3" y="3" width="7" height="7"></rect>
				<rect x="14" y="3" width="7" height="7"></rect>
				<rect x="3" y="14" width="7" height="7"></rect>
				<rect x="14" y="14" width="7" height="7"></rect>
			</>
		)}
	</svg>
);

// Sort Icon Component
const SortIcon = ({ column, currentSortColumn, sortDirection }) => {
	const isActive = currentSortColumn === column;
	return (
		<span className="sort-icon">
			<svg
				width="12"
				height="12"
				viewBox="0 0 24 24"
				fill="none"
				stroke="currentColor"
				strokeWidth="2"
			>
				<path
					d="M7 14l5-5 5 5"
					fill={isActive && sortDirection === "asc" ? "currentColor" : "none"}
					stroke={isActive && sortDirection === "asc" ? "currentColor" : "#ccc"}
					strokeWidth={isActive && sortDirection === "asc" ? 2 : 1}
				/>
				<path
					d="M7 10l5 5 5-5"
					fill={isActive && sortDirection === "desc" ? "currentColor" : "none"}
					stroke={
						isActive && sortDirection === "desc" ? "currentColor" : "#ccc"
					}
					strokeWidth={isActive && sortDirection === "desc" ? 2 : 1}
				/>
			</svg>
		</span>
	);
};

// Curation Filter Component
const CurationFilter = ({ currentFilter, onFilterChange, packageCounts }) => {
	const filters = [
		{ key: "all", label: "All Packages", count: packageCounts.all },
		{ key: "curated", label: "Curated Packages", count: packageCounts.curated },
		{
			key: "non-curated",
			label: "Non-Curated Packages",
			count: packageCounts.nonCurated,
		},
	];

	return (
		<div className="curation-filter">
			<div className="filter-buttons">
				{filters.map(({ key, label, count }) => (
					<button
						key={key}
						onClick={() => onFilterChange(key)}
						className={`filter-button ${currentFilter === key ? "active" : ""}`}
					>
						{label}
						<span className="filter-count">({count})</span>
					</button>
				))}
			</div>
		</div>
	);
};

const PackageDetailsModal = ({ isOpen, onClose, packageData }) => {
	const [activeTab, setActiveTab] = useState("overview");

	if (!isOpen || !packageData) return null;

	const packageVector = packageData.packageVector || {};
	const packageMetrics = packageData.packageMetrics || {};

	const formatMetricValue = (value, type = "number") => {
		if (value === null || value === undefined || value === "") return "N/A";

		switch (type) {
			case "currency":
				if (value >= 1000000) return `$${(value / 1000000).toFixed(2)}M`;
				if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
				return `$${parseFloat(value).toFixed(5)}`;
			case "percentage":
				return `${parseFloat(value).toFixed(5)}%`;
			case "number":
				if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
				if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
				return value.toLocaleString();
			case "date":
				if (Array.isArray(value) && value.length >= 3) {
					try {
						const [year, month, day] = value;
						return new Date(year, month - 1, day).toLocaleDateString();
					} catch {
						return "N/A";
					}
				}
				return value || "N/A";
			default:
				return value?.toString() || "N/A";
		}
	};

	const calculateRelevanceScore = (pkg) => {
		if (pkg.relevanceScore && typeof pkg.relevanceScore === "number") {
			return pkg.relevanceScore;
		}

		if (typeof pkg.score === "number") {
			// Packages sigmoid relevance curve (see constants.js)
			return relevanceFromDistance(parseFloat(pkg.score), "PACKAGES");
		}

		return 0;
	};

	const impressions = packageMetrics.impressions || 0;
	const fills = packageMetrics.fills || packageMetrics.impressions || 0;
	const spend = packageMetrics.totalSpendInUSD / 1000000 || 0;

	return (
		<div className="modal-overlay">
			<div className="modal-container">
				<div className="modal-header">
					<div className="header-content">
						<div>
							<h2 className="modal-title">{packageVector.packageName}</h2>
						</div>
						<button onClick={onClose} className="close-button">
							<X size={16} />
						</button>
					</div>
				</div>

				<div className="tab-navigation">
					<button
						className={`tab-button ${activeTab === "overview" ? "active" : ""}`}
						onClick={() => setActiveTab("overview")}
					>
						<BarChart3 size={16} className="tab-icon" />
						Overview
					</button>
					<button
						className={`tab-button ${activeTab === "technical" ? "active" : ""}`}
						onClick={() => setActiveTab("technical")}
					>
						<Activity size={16} className="tab-icon" />
						Technical
					</button>
					<button
						className={`tab-button ${activeTab === "deals" ? "active" : ""}`}
						onClick={() => setActiveTab("deals")}
					>
						<Target size={16} className="tab-icon" />
						Deals
					</button>
				</div>

				<div className="modal-body">
					<div className="tab-content">
						{activeTab === "overview" && (
							<div className="overview-content">
								{/* Key Performance Indicators */}
								<div className="kpi-section">
									<h4 className="section-title">Key Performance Indicators</h4>
									<div className="kpi-grid">
										<div className="kpi-card">
											<div className="kpi-value">
												{formatMetricValue(impressions, "number")}
											</div>
											<div className="kpi-label">IMPRESSIONS</div>
										</div>
										<div className="kpi-card">
											<div className="kpi-value">
												{formatMetricValue(fills, "number")}
											</div>
											<div className="kpi-label">FILLS</div>
										</div>
										<div className="kpi-card">
											<div className="kpi-value">
												{formatMetricValue(spend, "currency")}
											</div>
											<div className="kpi-label">SPEND</div>
										</div>
									</div>

									<div className="composite-scores">
										<div className="score-badge composite">
											<span>
												Composite:{" "}
												{packageData.compositeScore
													? packageData.compositeScore.toFixed(1)
													: "51.7"}
											</span>
										</div>
										<div className="score-badge search">
											<span>
												Search:{" "}
												{packageData.score
													? calculateRelevanceScore(packageData).toFixed(1)
													: "0.6"}
											</span>
										</div>
									</div>
								</div>

								{/* Performance Metrics */}
								<div className="metrics-section">
									<h4 className="section-title">Performance Metrics</h4>
									<div className="metrics-table">
										<div className="metrics-column">
											<h5 className="column-header">ENGAGEMENT</h5>
											<div className="metric-row">
												<span className="metric-name">Click Rate</span>
												<span className="metric-val">
													{formatMetricValue(
														packageMetrics.clickThroughRate,
														"percentage",
													)}
												</span>
											</div>
											<div className="metric-row">
												<span className="metric-name">Viewability</span>
												<span className="metric-val">
													{formatMetricValue(
														packageMetrics.viewability,
														"percentage",
													)}
												</span>
											</div>
											<div className="metric-row">
												<span className="metric-name">Clicks</span>
												<span className="metric-val">
													{formatMetricValue(
														packageMetrics.cprClicks,
														"number",
													)}
												</span>
											</div>
											<div className="metric-row">
												<span className="metric-name">Video Completions</span>
												<span className="metric-val">
													{formatMetricValue(
														packageMetrics.completeFirstOccurrence,
														"number",
													)}
												</span>
											</div>
										</div>

										<div className="metrics-column">
											<h5 className="column-header">REVENUE</h5>
											<div className="metric-row">
												<span className="metric-name">Bid CPM</span>
												<span className="metric-val">
													{formatMetricValue(
														packageMetrics.bidCpm / 1000000,
														"currency",
													)}
												</span>
											</div>
											<div className="metric-row">
												<span className="metric-name">
													Revenue Per Impression
												</span>
												<span className="metric-val">
													{formatMetricValue(
														packageMetrics.revenuePerImpression / 1000000,
														"currency",
													)}
												</span>
											</div>
											<div className="metric-row">
												<span className="metric-name">Cost Per Impression</span>
												<span className="metric-val">
													{formatMetricValue(
														packageMetrics.costPerImpression / 1000000,
														"currency",
													)}
												</span>
											</div>
											<div className="metric-row">
												<span className="metric-name">Bid Spend</span>
												<span className="metric-val">
													${packageMetrics.bidSpend / 1000000}
												</span>
											</div>
										</div>

										<div className="metrics-column">
											<h5 className="column-header">SCALE</h5>
											<div className="metric-row">
												<span className="metric-name">Total Requests</span>
												<span className="metric-val">
													{formatMetricValue(
														packageMetrics.totalMarketRequests || 0,
														"number",
													)}
												</span>
											</div>
											<div className="metric-row">
												<span className="metric-name">Impressions</span>
												<span className="metric-val">
													{formatMetricValue(
														packageMetrics.impressions,
														"number",
													)}
												</span>
											</div>
											<div className="metric-row">
												<span className="metric-name">Cookies</span>
												<span className="metric-val">0</span>
											</div>
											<div className="metric-row">
												<span className="metric-name">Fills</span>
												<span className="metric-val">
													{formatMetricValue(packageMetrics.fills, "number")}
												</span>
											</div>
										</div>
									</div>
								</div>

								{/* Device Distribution */}
								<div className="device-section">
									<h4 className="section-title">Device Distribution</h4>
									<div className="device-placeholder">
										<p>
											Device distribution data:{" "}
											{packageVector.deviceTypes || "Not specified"}
										</p>
										<p>Ad Placement: {packageVector.adPlacement || "N/A"}</p>
										<p>
											Distribution Channel:{" "}
											{packageVector.distributionChannel || "N/A"}
										</p>
									</div>
								</div>
							</div>
						)}

						{activeTab === "technical" && (
							<div className="technical-content">
								<div className="technical-metrics-section">
									<h4 className="section-title">Technical Metrics</h4>
									<div className="technical-grid">
										<div className="technical-column">
											<h5 className="column-header">BID METRICS</h5>
											<div className="technical-item">
												<span className="technical-label">Non-Zero Bids</span>
												<span className="technical-value">
													{formatMetricValue(
														packageMetrics.nonZeroBids,
														"number",
													)}
												</span>
											</div>
											<div className="technical-item">
												<span className="technical-label">
													Non-Zero Bids Value
												</span>
												<span className="technical-value">
													{formatMetricValue(
														packageMetrics.nonZeroBidsValue / 1000000,
														"currency",
													)}
												</span>
											</div>
											<div className="technical-item">
												<span className="technical-label">Bid Spend</span>
												<span className="technical-value">
													${packageMetrics.bidSpend / 1000000}
												</span>
											</div>
											<div className="technical-item">
												<span className="technical-label">Bid Guidance</span>
												<span className="technical-value">
													{formatMetricValue(
														packageMetrics.bidGuidance / 1000000,
														"currency",
													)}
												</span>
											</div>
											<div className="technical-item">
												<span className="technical-label">Win Rate</span>
												<span className="technical-value">
													{formatMetricValue(
														packageMetrics.winRate,
														"percentage",
													)}
												</span>
											</div>
										</div>

										<div className="technical-column">
											<h5 className="column-header">REVENUE METRICS</h5>
											<div className="technical-item">
												<span className="technical-label">
													Revenue Per Impression
												</span>
												<span className="technical-value">
													{formatMetricValue(
														packageMetrics.revenuePerImpression / 1000000,
														"currency",
													)}
												</span>
											</div>
											<div className="technical-item">
												<span className="technical-label">
													Cost Per Impression
												</span>
												<span className="technical-value">
													{formatMetricValue(
														packageMetrics.costPerImpression / 1000000,
														"currency",
													)}
												</span>
											</div>
											<div className="technical-item">
												<span className="technical-label">
													Publisher Revenue (USD)
												</span>
												<span className="technical-value">
													{formatMetricValue(
														packageMetrics.publisherRevenueUSD / 1000000,
														"currency",
													)}
												</span>
											</div>
											<div className="technical-item">
												<span className="technical-label">OpenX Win Rate</span>
												<span className="technical-value">
													{formatMetricValue(
														packageMetrics.oxWinRate * 100,
														"percentage",
													)}
												</span>
											</div>
											<div className="technical-item">
												<span className="technical-label">Public Win Rate</span>
												<span className="technical-value">
													{formatMetricValue(
														packageMetrics.pubWinRate * 100,
														"percentage",
													)}
												</span>
											</div>
										</div>

										<div className="technical-column">
											<h5 className="column-header">TRAFFIC METRICS</h5>
											<div className="technical-item">
												<span className="technical-label">
													Total Market Requests
												</span>
												<span className="technical-value">
													{formatMetricValue(
														packageMetrics.totalMarketRequests,
														"number",
													)}
												</span>
											</div>
											<div className="technical-item">
												<span className="technical-label">
													Total Ad Requests
												</span>
												<span className="technical-value">
													{formatMetricValue(
														packageMetrics.totalAdRequests,
														"number",
													)}
												</span>
											</div>
											<div className="technical-item">
												<span className="technical-label">
													Private Market Impressions
												</span>
												<span className="technical-value">
													{formatMetricValue(
														packageMetrics.privateMarketImpressions,
														"number",
													)}
												</span>
											</div>
											<div className="technical-item">
												<span className="technical-label">
													Impressions with Click Macro
												</span>
												<span className="technical-value">
													{formatMetricValue(
														packageMetrics.impressionsWithClickMacro,
														"number",
													)}
												</span>
											</div>
											<div className="technical-item">
												<span className="technical-label">
													Viewability Measures
												</span>
												<span className="technical-value">
													{formatMetricValue(
														packageMetrics.viewabilityMeasures,
														"number",
													)}
												</span>
											</div>
										</div>
									</div>
								</div>

								{/* Additional Technical Details */}
								<div className="technical-details-section">
									<h4 className="section-title">Technical Configuration</h4>
									<div className="info-grid">
										<div className="info-item">
											<div className="info-label">PACKAGE ID</div>
											<div className="info-value">
												{packageVector.packageId || "N/A"}
											</div>
										</div>
										<div className="info-item">
											<div className="info-label">PACKAGE TYPE</div>
											<div className="info-value">
												{packageVector.packageType || "N/A"}
											</div>
										</div>
										<div className="info-item">
											<div className="info-label">REVISION</div>
											<div className="info-value">
												{packageVector.revision || "N/A"}
											</div>
										</div>
										<div className="info-item">
											<div className="info-label">DEVICE TYPE IDS</div>
											<div className="info-value">
												{packageVector.deviceTypeIds || "N/A"}
											</div>
										</div>
										<div className="info-item">
											<div className="info-label">ACCOUNT ID</div>
											<div className="info-value">
												{packageVector.accountId || "N/A"}
											</div>
										</div>
										<div className="info-item">
											<div className="info-label">PUBLISHER ACCOUNT NK</div>
											<div className="info-value">
												{packageVector.publisherAccountNk || "N/A"}
											</div>
										</div>
										<div className="info-item">
											<div className="info-label">CLONED FROM</div>
											<div className="info-value">
												{packageVector.clonedFrom || "N/A"}
											</div>
										</div>
										<div className="info-item">
											<div className="info-label">HAS META</div>
											<div className="info-value">
												{packageVector.hasMeta ? "Yes" : "No"}
											</div>
										</div>
										<div className="info-item">
											<div className="info-label">EXCLUDE MFA</div>
											<div className="info-value">
												{packageVector.excludeMfa ? "Yes" : "No"}
											</div>
										</div>
									</div>
								</div>
							</div>
						)}

						{activeTab === "deals" && (
							<div className="deals-content">
								{/* All Associated Deal IDs */}
								<div className="deal-ids-section">
									<h4 className="section-title">
										Associated Deal IDs ({packageData.dealIds?.length || 1})
									</h4>
									<div className="deal-ids-grid">
										{(packageData.dealIds || [packageVector.dealId])
											.filter(Boolean)
											.map((dealId, index) => (
												<div
													key={`${dealId}-${index}`}
													className="deal-id-item"
												>
													<div className="deal-id-label">Deal #{index + 1}</div>
													<div className="deal-id-value">
														<span className="deal-id-code">{dealId}</span>
														<button
															className="copy-btn"
															onClick={() => {
																navigator.clipboard.writeText(dealId);
															}}
															title="Copy Deal ID"
														>
															📋
														</button>
													</div>
												</div>
											))}
									</div>
								</div>

								{/* Deal Information - Show all deals with comma-separated values */}
								<div className="basic-info-section">
									<h4 className="section-title">Deal Information</h4>
									{packageData.dealCount > 1 && (
										<div className="info-note">
											ℹ️ This package has {packageData.dealCount} deals. Values
											below are shown for each deal separated by commas.
										</div>
									)}
									<div className="info-grid">
										<div className="info-item">
											<div className="info-label">DEAL ID(S)</div>
											<div className="info-value">
												{packageData.dealSpecificInfo
													? packageData.dealSpecificInfo
															.map((d) => d.dealId)
															.filter(Boolean)
															.join(", ")
													: (packageData.dealIds || [packageVector.dealId])
															.filter(Boolean)
															.join(", ") || "N/A"}
											</div>
										</div>
										<div className="info-item">
											<div className="info-label">DEAL NAME(S)</div>
											<div className="info-value">
												{packageData.dealSpecificInfo
													? packageData.dealSpecificInfo
															.map((d) => d.dealName || "N/A")
															.join(", ")
													: packageVector.dealName || "N/A"}
											</div>
										</div>
										<div className="info-item">
											<div className="info-label">DEAL STATUS(ES)</div>
											<div className="info-value">
												{packageData.dealSpecificInfo
													? packageData.dealSpecificInfo
															.map((d) => d.dealStatus || "N/A")
															.join(", ")
													: packageVector.dealStatus || "N/A"}
											</div>
										</div>
										<div className="info-item">
											<div className="info-label">DEAL SOURCE(S)</div>
											<div className="info-value">
												{packageData.dealSpecificInfo
													? packageData.dealSpecificInfo
															.map((d) => d.dealSource || "N/A")
															.join(", ")
													: packageVector.dealSource || "N/A"}
											</div>
										</div>
										<div className="info-item">
											<div className="info-label">RATE CARD CPM(S)</div>
											<div className="info-value">
												{packageData.dealSpecificInfo
													? packageData.dealSpecificInfo
															.map((d) =>
																formatMetricValue(d.rateCardCpm, "currency"),
															)
															.join(", ")
													: formatMetricValue(
															packageVector.rateCardCpm,
															"currency",
														)}
											</div>
										</div>
										<div className="info-item">
											<div className="info-label">DEAL PRICE(S)</div>
											<div className="info-value">
												{packageData.dealSpecificInfo
													? packageData.dealSpecificInfo
															.map((d) =>
																formatMetricValue(d.dealPrice, "currency"),
															)
															.join(", ")
													: formatMetricValue(
															packageVector.dealPrice,
															"currency",
														)}
											</div>
										</div>
										<div className="info-item">
											<div className="info-label">START DATE(S)</div>
											<div className="info-value">
												{packageData.dealSpecificInfo
													? packageData.dealSpecificInfo
															.map((d) =>
																formatMetricValue(d.dealStartDate, "date"),
															)
															.join(", ")
													: formatMetricValue(
															packageVector.dealStartDate,
															"date",
														)}
											</div>
										</div>
										<div className="info-item">
											<div className="info-label">END DATE(S)</div>
											<div className="info-value">
												{packageData.dealSpecificInfo
													? packageData.dealSpecificInfo
															.map((d) =>
																formatMetricValue(d.dealEndDate, "date"),
															)
															.join(", ")
													: formatMetricValue(
															packageVector.dealEndDate,
															"date",
														)}
											</div>
										</div>
										<div className="info-item">
											<div className="info-label">ASSOCIATED ADVERTISERS</div>
											<div className="info-value">
												{packageVector.associatedAdvertisers || "N/A"}
											</div>
										</div>
										<div className="info-item">
											<div className="info-label">IS CURATED</div>
											<div className="info-value">
												{packageVector.isCurated ? "Yes" : "No"}
											</div>
										</div>
										<div className="info-item">
											<div className="info-label">DISCOUNTED CPM</div>
											<div className="info-value">
												{formatMetricValue(
													packageVector.discountedCpm,
													"currency",
												)}
											</div>
										</div>
										<div className="info-item">
											<div className="info-label">DEAL DELETED</div>
											<div className="info-value">
												{packageVector.dealDeleted === "false" ? "No" : "Yes"}
											</div>
										</div>
									</div>
								</div>

								{/* Package Categories */}
								<div className="category-section">
									<h4 className="section-title">Package Categories</h4>
									<div className="info-grid">
										<div className="info-item">
											<div className="info-label">CATEGORY L1</div>
											<div className="info-value">
												{packageVector.packageCategoryL1 || "N/A"}
											</div>
										</div>
										<div className="info-item">
											<div className="info-label">CATEGORY L2</div>
											<div className="info-value">
												{packageVector.packageCategoryL2 || "N/A"}
											</div>
										</div>
										<div className="info-item">
											<div className="info-label">CATEGORY L3</div>
											<div className="info-value">
												{packageVector.packageCategoryL3 || "N/A"}
											</div>
										</div>
										<div className="info-item">
											<div className="info-label">PRIMARY VERTICAL</div>
											<div className="info-value">
												{packageVector.primaryVertical || "N/A"}
											</div>
										</div>
										<div className="info-item">
											<div className="info-label">COUNTRY</div>
											<div className="info-value">
												{packageVector.country?.toUpperCase() || "N/A"}
											</div>
										</div>
										<div className="info-item">
											<div className="info-label">AUDIENCE TYPE</div>
											<div className="info-value">
												{packageVector.audienceType || "N/A"}
											</div>
										</div>
									</div>
								</div>

								{/* OXTL Configuration */}
								<div className="oxtl-section">
									<h4 className="section-title">OXTL Configuration</h4>
									<div className="oxtl-content">
										<div className="info-item">
											<div className="info-label">OXTL TARGETING</div>
											<div className="info-value oxtl-text">
												{packageVector.oxtl || "N/A"}
											</div>
										</div>
									</div>
								</div>
							</div>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};

const aggregatePackagesByDealId = (results) => {
	const packageMap = new Map();

	results.forEach((pkg) => {
		const packageId = pkg.packageVector?.packageId || pkg.packageId;

		if (!packageMap.has(packageId)) {
			// First occurrence - initialize
			packageMap.set(packageId, {
				...pkg,
				dealIds: [pkg.packageVector?.dealId].filter(Boolean),
				dealCount: 1,

				// Aggregated package-level metrics
				packageMetrics: { ...pkg.packageMetrics },

				// NEW: Store individual deal-level metrics
				dealMetrics: [
					{
						dealId: pkg.packageVector?.dealId,
						metrics: { ...pkg.packageMetrics },
					},
				],

				// Deal-specific information (non-metric data)
				dealSpecificInfo: [
					{
						dealId: pkg.packageVector?.dealId,
						dealName: pkg.packageVector?.dealName,
						dealStatus: pkg.packageVector?.dealStatus,
						dealPrice: pkg.packageVector?.dealPrice,
						rateCardCpm: pkg.packageVector?.rateCardCpm,
						dealSource: pkg.packageVector?.dealSource,
						dealStartDate: pkg.packageVector?.dealStartDate,
						dealEndDate: pkg.packageVector?.dealEndDate,
					},
				],
			});
		} else {
			// Subsequent occurrence - aggregate
			const existing = packageMap.get(packageId);
			const dealId = pkg.packageVector?.dealId;

			// Add deal ID if not already present
			if (dealId && !existing.dealIds.includes(dealId)) {
				existing.dealIds.push(dealId);

				// Store this deal's individual metrics (NEW)
				existing.dealMetrics.push({
					dealId: dealId,
					metrics: { ...pkg.packageMetrics },
				});

				// Store deal-specific information
				existing.dealSpecificInfo.push({
					dealId: dealId,
					dealName: pkg.packageVector?.dealName,
					dealStatus: pkg.packageVector?.dealStatus,
					dealPrice: pkg.packageVector?.dealPrice,
					rateCardCpm: pkg.packageVector?.rateCardCpm,
					dealSource: pkg.packageVector?.dealSource,
					dealStartDate: pkg.packageVector?.dealStartDate,
					dealEndDate: pkg.packageVector?.dealEndDate,
				});
			}

			existing.dealCount++;

			// Aggregate ADDITIVE metrics only
			const metrics = existing.packageMetrics;
			const newMetrics = pkg.packageMetrics;

			if (newMetrics) {
				// Sum additive metrics
				metrics.impressions =
					(metrics.impressions || 0) + (newMetrics.impressions || 0);
				metrics.fills = (metrics.fills || 0) + (newMetrics.fills || 0);
				metrics.nonZeroBids =
					(metrics.nonZeroBids || 0) + (newMetrics.nonZeroBids || 0);
				metrics.cprClicks =
					(metrics.cprClicks || 0) + (newMetrics.cprClicks || 0);
				metrics.impressionsWithClickMacro =
					(metrics.impressionsWithClickMacro || 0) +
					(newMetrics.impressionsWithClickMacro || 0);
				metrics.completeFirstOccurrence =
					(metrics.completeFirstOccurrence || 0) +
					(newMetrics.completeFirstOccurrence || 0);
				metrics.startFirstOccurrence =
					(metrics.startFirstOccurrence || 0) +
					(newMetrics.startFirstOccurrence || 0);
				metrics.viewables =
					(metrics.viewables || 0) + (newMetrics.viewables || 0);
				metrics.viewabilityMeasures =
					(metrics.viewabilityMeasures || 0) +
					(newMetrics.viewabilityMeasures || 0);
				metrics.dealVolume =
					(metrics.dealVolume || 0) + (newMetrics.dealVolume || 0);
				metrics.nonZeroBidsValue =
					(metrics.nonZeroBidsValue || 0) + (newMetrics.nonZeroBidsValue || 0);
				metrics.bidSpend = (metrics.bidSpend || 0) + (newMetrics.bidSpend || 0);
				metrics.totalSpendInUSD =
					(metrics.totalSpendInUSD || 0) + (newMetrics.totalSpendInUSD || 0);
				metrics.publisherRevenueUSD =
					(metrics.publisherRevenueUSD || 0) +
					(newMetrics.publisherRevenueUSD || 0);
				metrics.totalMarketRequests =
					(metrics.totalMarketRequests || 0) +
					(newMetrics.totalMarketRequests || 0);
				metrics.totalAdRequests =
					(metrics.totalAdRequests || 0) + (newMetrics.totalAdRequests || 0);
				metrics.privateMarketImpressions =
					(metrics.privateMarketImpressions || 0) +
					(newMetrics.privateMarketImpressions || 0);

				// Recalculate DERIVED metrics from aggregated totals
				if (metrics.impressions > 0) {
					metrics.clickThroughRate =
						(metrics.cprClicks / metrics.impressions) * 100 || 0;
					metrics.revenuePerImpression =
						metrics.publisherRevenueUSD / metrics.impressions || 0;
					metrics.costPerImpression =
						metrics.totalSpendInUSD / metrics.impressions || 0;
				}

				if (metrics.viewabilityMeasures > 0) {
					metrics.viewability =
						(metrics.viewables / metrics.viewabilityMeasures) * 100 || 0;
				}

				if (metrics.startFirstOccurrence > 0) {
					metrics.vtr =
						(metrics.completeFirstOccurrence / metrics.startFirstOccurrence) *
							100 || 0;
				}

				if (metrics.nonZeroBids > 0) {
					metrics.bidCpm =
						(metrics.nonZeroBidsValue / metrics.nonZeroBids) * 1000 || 0;
					metrics.oxWinRate =
						(metrics.impressions / metrics.nonZeroBids) * 100 || 0;
				}

				if (metrics.fills > 0) {
					metrics.pubWinRate = (metrics.impressions / metrics.fills) * 100 || 0;
				}

				if (metrics.fills > 0 && metrics.nonZeroBids > 0) {
					metrics.winRate = (metrics.fills / metrics.nonZeroBids) * 100 || 0;
				}
			}
		}
	});

	return Array.from(packageMap.values());
};

export const PackageResultsList = ({
	results = [],
	isLoading = false,
	error = null,
	onLoadMore = null,
	hasMore = false,
	className = "",
	selectedResults = [],
	onResultSelect = () => {},
	onSelectAll = () => {},
	isAllSelected = false,
	onExport = () => {},
	isExporting = false,
	// Add pagination props
	currentPage = 1,
	totalPages = 1,
	totalItems = 0,
	itemsPerPage = 25,
	onPageChange = () => {},
	onPageSizeChange = () => {},
	hasResults = true,
	resultsCount = 0,
	sortColumn = null,
	sortDirection = "asc",
	onSortChange = () => {},
	curationFilter = "all",
	onCurationFilterChange = () => {},
	packageCounts = { all: 0, curated: 0, nonCurated: 0 },
	excludedPackageIds,
}) => {
	const [viewMode, setViewMode] = useState("card");
	const [isSearchVisible, setIsSearchVisible] = useState(false);
	const [localSearchTerm, setLocalSearchTerm] = useState("");

	// Modal states
	const [isTargetingModalOpen, setIsTargetingModalOpen] = useState(false);
	const [selectedPackageForTargeting, setSelectedPackageForTargeting] =
		useState(null);
	const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
	const [selectedPackageForDetails, setSelectedPackageForDetails] =
		useState(null);
	const [isCreatePackageModalOpen, setIsCreatePackageModalOpen] =
		useState(false);

	const aggregatedResults = useMemo(() => {
		return aggregatePackagesByDealId(results);
	}, [results]);

	// Filter out excluded packages
	const filteredResults = aggregatedResults.filter((pkg) => {
		const packageId = pkg.packageVector?.packageId || pkg.packageId || pkg.id;
		return !excludedPackageIds.includes(packageId);
	});

	const searchFilteredResults = useMemo(() => {
		if (!localSearchTerm.trim()) {
			return filteredResults;
		}

		const searchLower = localSearchTerm.toLowerCase();

		return filteredResults.filter((pkg) => {
			const packageName = pkg.packageVector?.packageName || "";
			return packageName.toLowerCase().includes(searchLower);
		});
	}, [filteredResults, localSearchTerm]);

	const sortedData = searchFilteredResults;

	// Calculate relevance score from the score field
	const calculateRelevanceScore = (pkg) => {
		if (pkg.relevanceScore && typeof pkg.relevanceScore === "number") {
			return pkg.relevanceScore;
		}

		if (typeof pkg.score === "number") {
			// Packages sigmoid relevance curve (see constants.js)
			return relevanceFromDistance(parseFloat(pkg.score), "PACKAGES");
		}

		return 0;
	};

	// Format metrics for display
	const formatMetric = (value, type = "number") => {
		if (!value && value !== 0) return "N/A";

		switch (type) {
			case "currency":
				if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
				if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
				return `$${value.toFixed(2)}`;
			case "percentage":
				if (value < 1) return `${(value).toFixed(4)}%`;
				return `${(value).toFixed(2)}%`;
			case "number":
				if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
				if (value >= 1000) return `${(value / 1000).toFixed(0)}K`;
				return value.toString();
			default:
				return value.toString();
		}
	};

	const formatDate = (dateArray) => {
		if (!dateArray || !Array.isArray(dateArray) || dateArray.length < 3)
			return "N/A";
		try {
			const [year, month, day] = dateArray;
			return new Date(year, month - 1, day).toLocaleDateString();
		} catch {
			return "N/A";
		}
	};

	const getRelevanceClass = (score) => {
		if (score >= 90) return "excellent";
		if (score >= 80) return "great";
		if (score >= 70) return "acceptable";
		if (score >= 60) return "poor";
		return "irrelevant";
	};

	const getMatchPercentage = (pkg) => {
		const relevance = calculateRelevanceScore(pkg);
		return Math.round(relevance);
	};

	const formatNumber = (value) => {
		if (value === null || value === undefined || value === "") return "N/A";
		if (typeof value === "number") {
			return value.toLocaleString();
		}
		if (typeof value === "string" && !isNaN(value)) {
			return parseInt(value).toLocaleString();
		}
		return value;
	};

	const formatPercentage = (value) => {
		if (value === null || value === undefined || value === "") return "N/A";
		if (typeof value === "number") return `${(value).toFixed(2)}%`;
		if (typeof value === "string" && !isNaN(value))
			return `${(parseFloat(value) * 100).toFixed(2)}%`;
		return value;
	};

	const getStatusColor = (status) => {
		switch (status?.toLowerCase()) {
			case "active":
				return "#28a745";
			case "pending":
				return "#ffc107";
			case "paused":
				return "#6c757d";
			case "inactive":
				return "#dc3545";
			default:
				return "#6c757d";
		}
	};

	// Handle targeting button click
	const handleTargetingClick = (pkg) => {
		setSelectedPackageForTargeting(pkg);
		setIsTargetingModalOpen(true);
	};

	const closeTargetingModal = () => {
		setIsTargetingModalOpen(false);
		setSelectedPackageForTargeting(null);
	};

	// Handle details button click
	const handleDetailsClick = (pkg) => {
		setSelectedPackageForDetails(pkg);
		setIsDetailsModalOpen(true);
	};

	const closeDetailsModal = () => {
		setIsDetailsModalOpen(false);
		setSelectedPackageForDetails(null);
	};

	// Handle curation filter change
	const handleCurationFilterChange = (filter) => {
		// Call parent component's curation filter handler
		onCurationFilterChange(filter);
	};

	// Sorting functionality
	const handleSort = (column) => {
		let newDirection = "asc";

		if (sortColumn === column) {
			newDirection = sortDirection === "asc" ? "desc" : "asc";
		}

		// Call parent component's sort handler instead of local state
		onSortChange(column, newDirection);
	};

	const clearSort = () => {
		onSortChange(null, "asc");
	};

	const toggleViewMode = () => {
		setViewMode(viewMode === "card" ? "table" : "card");
	};

	const handleSearchChange = (event) => {
		const value = event.target.value;
		setLocalSearchTerm(value);
	};

	const clearSearch = () => {
		setLocalSearchTerm("");
		setIsSearchVisible(false);
	};

	const renderPackageCard = (pkg, index) => {
		const packageVector = pkg.packageVector || {};
		const packageMetrics = pkg.packageMetrics || {};
		const matchPercentage = getMatchPercentage(pkg);
		const isSelected = selectedResults.includes(
			pkg.packageId || pkg.id || index,
		);

		return (
			<div
				key={pkg.packageId || pkg.id || index}
				className={`package-card ${isSelected ? "selected" : ""}`}
			>
				{/* Match Percentage Badge */}
				<div className="match-badge">{matchPercentage}% Match</div>

				{/* Curation Badge */}
				{packageVector.isCurated && (
					<div className="curation-badge">
						<Star size={12} />
						Curated
					</div>
				)}

				{/* Card Content */}
				<div className="card-content">
					<div className="card-header">
						<h3
							className="package-title"
							title={packageVector.packageName || `Package ${index + 1}`}
						>
							{packageVector.packageName || `Package ${index + 1}`}
						</h3>
						<div className="package-subtitle">
							{packageVector.primaryVertical || "N/A"}
						</div>
						<div className="package-subtitle">
							{packageVector.adPlacement || "N/A"}
						</div>
					</div>

					{/* Important Metrics - Row 1 */}
					<div className="metrics-row">
						<div className="metric">
							<div className="metric-label">Impressions</div>
							<div className="metric-value">
								{formatMetric(packageMetrics.impressions, "number")}
							</div>
						</div>
						<div className="metric">
							<div className="metric-label">CPM</div>
							<div className="metric-value">
								{formatMetric(packageMetrics.bidCpm / 1000000, "currency")}
							</div>
						</div>
						<div className="metric">
							<div className="metric-label">Bid Guidance</div>
							<div className="metric-value">
								{formatMetric(packageMetrics.bidGuidance / 1000000, "currency")}
							</div>
						</div>
					</div>

					{/* Important Metrics - Row 2 */}
					<div className="metrics-row">
						<div className="metric">
							<div className="metric-label">CTR/VTR</div>
							<div className="metric-value">
								{packageMetrics.clickThroughRate
									? formatMetric(packageMetrics.clickThroughRate, "percentage")
									: "-"}
								/
								{packageMetrics.vtr
									? formatMetric(packageMetrics.vtr, "percentage")
									: "-"}
							</div>
						</div>
						<div className="metric">
							<div className="metric-label">Spend</div>
							<div className="metric-value">
								${(packageMetrics.totalSpendInUSD / 1000000).toFixed(3)}
							</div>
						</div>
						<div className="metric">
							<div className="metric-label">Viewability</div>
							<div className="metric-value">
								{packageMetrics.viewability
									? formatMetric(packageMetrics.viewability, "percentage")
									: "N/A"}
							</div>
						</div>
					</div>
					<div className="metrics-row">
						<div className="metric">
							<div className="metric-label">Device Types</div>
							<div className="metric-value-device">
								{packageVector.deviceTypes ? packageVector.deviceTypes : "N/A"}
							</div>
						</div>
					</div>

					<div className="card-actions">
						<input
							type="checkbox"
							checked={isSelected}
							onChange={() => onResultSelect(pkg.packageId || pkg.id || index)}
							className="package-checkbox"
						/>
						<button
							className="btn-details"
							onClick={() => handleDetailsClick(pkg)}
						>
							<Info size={14} />
							Details
						</button>
						<button
							className="btn-targeting"
							onClick={() => handleTargetingClick(pkg)}
						>
							<TrendingUp size={14} />
							Targeting
						</button>
						<div className="days-remaining">
							{packageVector.dealEndDate
								? formatDate(packageVector.dealEndDate)
								: "N/A"}
						</div>
					</div>

					<div className="status-indicator">
						<span
							className="status-badge"
							style={{
								backgroundColor: getStatusColor(packageVector.dealStatus),
							}}
						>
							{packageVector.dealStatus || "active"}
						</span>
					</div>
				</div>
			</div>
		);
	};

	if (error) {
		return (
			<div className="results-container">
				<div className="results-section">
					<h2 className="section-title">Package Results</h2>
					<div className="error-state">
						<div className="error-icon">⚠️</div>
						<h3>Error Loading Results</h3>
						<p>{error}</p>
						<button
							onClick={() => window.location.reload()}
							className="retry-button"
						>
							Retry
						</button>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="results-container">
			<div className="results-section">
				<div className="results-header">
					<h2 className="section-title">
						Package Results (
						{filteredResults.length > 0
							? filteredResults.length.toLocaleString()
							: 0}{" "}
						of {totalItems > 0 ? totalItems.toLocaleString() : results.length}{" "}
						packages)
						{sortColumn && (
							<span className="sort-status">
								• Sorted by{" "}
								{sortColumn.replace(/([A-Z])/g, " $1").toLowerCase()} (
								{sortDirection === "asc" ? "A-Z" : "Z-A"})
							</span>
						)}
						{sortColumn && (
							<button
								onClick={clearSort}
								className="clear-sort-btn"
								title="Clear sorting"
							>
								Clear Sort
							</button>
						)}
					</h2>

					<div className="results-actions">
						{/* <button
              onClick={() => setIsCreatePackageModalOpen(true)}
              className="create-package-btn"
            >
              <Package size={16} />
              Create Package
            </button> */}
						<button
							onClick={toggleViewMode}
							className="view-toggle-btn"
							title={`Switch to ${viewMode === "card" ? "table" : "card"} view`}
						>
							<ViewToggleIcon isTiled={viewMode === "card"} />
							{viewMode === "card" ? "Table View" : "Card View"}
						</button>
					</div>
				</div>

				{/* Curation Filter - Always show */}
				<CurationFilter
					currentFilter={curationFilter}
					onFilterChange={handleCurationFilterChange}
					packageCounts={packageCounts}
				/>

				{/* Content Area - either results or empty state */}
				{searchFilteredResults.length === 0 ? (
					<div className="empty-state">
						<Package size={64} className="empty-icon" />
						<h3>
							{localSearchTerm.trim()
								? `No packages found matching "${localSearchTerm}"`
								: `No ${curationFilter === "curated" ? "curated " : curationFilter === "non-curated" ? "non-curated " : ""}packages found`}
						</h3>
						<p>
							{localSearchTerm.trim()
								? "Try a different search term or clear the search to see all packages."
								: curationFilter === "curated" && packageCounts.curated === 0
									? 'There are no curated packages available. Try switching to "All Packages" or "Non-Curated Packages".'
									: "Try adjusting your search terms or filters to find relevant packages."}
						</p>
						{localSearchTerm.trim() && (
							<button
								onClick={() => setLocalSearchTerm("")}
								className="retry-button"
							>
								Clear Search
							</button>
						)}
					</div>
				) : (
					<>
						{viewMode === "card" ? (
							<div className="results-grid">
								{sortedData.map((pkg, index) => renderPackageCard(pkg, index))}
							</div>
						) : (
							<div className="results-table-container">
								<table className="results-table">
									<thead>
										<tr>
											<th className="checkbox-column">
												<input
													type="checkbox"
													checked={isAllSelected}
													onChange={() => onSelectAll(!isAllSelected)}
													title="Select All"
												/>
											</th>
											<th className="name-column">
												{isSearchVisible ? (
													<div className="search-header-container">
														<input
															type="text"
															placeholder="Search by package name..."
															value={localSearchTerm}
															onChange={handleSearchChange}
															className="package-search-input"
															autoFocus
														/>
														<button
															className="close-search-btn"
															onClick={clearSearch}
															title="Close search"
														>
															✖
														</button>
													</div>
												) : (
													<div className="package-header">
														<div
															className={`sortable-header ${sortColumn === "packageName" ? "active" : ""}`}
															onClick={() => handleSort("packageName")}
														>
															<span className="header-text">Package Name</span>
															<SortIcon
																column="packageName"
																currentSortColumn={sortColumn}
																sortDirection={sortDirection}
															/>
														</div>
														<button
															className="search-toggle-btn"
															onClick={() => setIsSearchVisible(true)}
															title="Search packages"
														>
															<Search size={14} />
														</button>
													</div>
												)}
											</th>
											<th className="category-column">
												<div
													className={`sortable-header ${sortColumn === "category" ? "active" : ""}`}
													onClick={() => handleSort("category")}
												>
													<span className="header-text">Category</span>
													<SortIcon
														column="category"
														currentSortColumn={sortColumn}
														sortDirection={sortDirection}
													/>
												</div>
											</th>
											<th className="vertical-column">
												<div
													className={`sortable-header ${sortColumn === "vertical" ? "active" : ""}`}
													onClick={() => handleSort("vertical")}
												>
													<span className="header-text">Vertical</span>
													<SortIcon
														column="vertical"
														currentSortColumn={sortColumn}
														sortDirection={sortDirection}
													/>
												</div>
											</th>
											<th className="curated-column">
												<div
													className={`sortable-header ${sortColumn === "curated" ? "active" : ""}`}
													onClick={() => handleSort("curated")}
												>
													<span className="header-text">Curated</span>
													<SortIcon
														column="curated"
														currentSortColumn={sortColumn}
														sortDirection={sortDirection}
													/>
												</div>
											</th>
											<th className="status-column">
												<div
													className={`sortable-header ${sortColumn === "status" ? "active" : ""}`}
													onClick={() => handleSort("status")}
												>
													<span className="header-text">Status</span>
													<SortIcon
														column="status"
														currentSortColumn={sortColumn}
														sortDirection={sortDirection}
													/>
												</div>
											</th>
											<th className="relevance-column">
												<div
													className={`sortable-header ${sortColumn === "relevance" ? "active" : ""}`}
													onClick={() => handleSort("relevance")}
												>
													<span className="header-text">Relevance</span>
													<SortIcon
														column="relevance"
														currentSortColumn={sortColumn}
														sortDirection={sortDirection}
													/>
												</div>
											</th>
											<th className="score-column">
												<div
													className={`sortable-header ${sortColumn === "compositeScore" ? "active" : ""}`}
													onClick={() => handleSort("compositeScore")}
												>
													<span className="header-text">Composite Score</span>
													<SortIcon
														column="compositescore"
														currentSortColumn={sortColumn}
														sortDirection={sortDirection}
													/>
												</div>
											</th>
											<th className="deal-price-column">
												<div
													className={`sortable-header ${sortColumn === "dealPrice" ? "active" : ""}`}
													onClick={() => handleSort("dealPrice")}
												>
													<span className="header-text">Deal Price</span>
													<SortIcon
														column="dealPrice"
														currentSortColumn={sortColumn}
														sortDirection={sortDirection}
													/>
												</div>
											</th>
											<th className="ctr-column">
												<div
													className={`sortable-header ${sortColumn === "ctr" ? "active" : ""}`}
													onClick={() => handleSort("ctr")}
												>
													<span className="header-text">CTR</span>
													<SortIcon
														column="ctr"
														currentSortColumn={sortColumn}
														sortDirection={sortDirection}
													/>
												</div>
											</th>
											<th className="viewability-column">
												<div
													className={`sortable-header ${sortColumn === "viewability" ? "active" : ""}`}
													onClick={() => handleSort("viewability")}
												>
													<span className="header-text">Viewability</span>
													<SortIcon
														column="viewability"
														currentSortColumn={sortColumn}
														sortDirection={sortDirection}
													/>
												</div>
											</th>
											<th className="impressions-column">
												<div
													className={`sortable-header ${sortColumn === "impressions" ? "active" : ""}`}
													onClick={() => handleSort("impressions")}
												>
													<span className="header-text">Impressions</span>
													<SortIcon
														column="impressions"
														currentSortColumn={sortColumn}
														sortDirection={sortDirection}
													/>
												</div>
											</th>
											<th className="fills-column">
												<div
													className={`sortable-header ${sortColumn === "fills" ? "active" : ""}`}
													onClick={() => handleSort("fills")}
												>
													<span className="header-text">Fills</span>
													<SortIcon
														column="fills"
														currentSortColumn={sortColumn}
														sortDirection={sortDirection}
													/>
												</div>
											</th>
											<th className="clicks-column">
												<div
													className={`sortable-header ${sortColumn === "clicks" ? "active" : ""}`}
													onClick={() => handleSort("clicks")}
												>
													<span className="header-text">Clicks</span>
													<SortIcon
														column="clicks"
														currentSortColumn={sortColumn}
														sortDirection={sortDirection}
													/>
												</div>
											</th>
											<th className="bid-cpm-column">
												<div
													className={`sortable-header ${sortColumn === "bidCpm" ? "active" : ""}`}
													onClick={() => handleSort("bidCpm")}
												>
													<span className="header-text">Bid CPM</span>
													<SortIcon
														column="bidCpm"
														currentSortColumn={sortColumn}
														sortDirection={sortDirection}
													/>
												</div>
											</th>
											<th className="win-rate-column">
												<div
													className={`sortable-header ${sortColumn === "winRate" ? "active" : ""}`}
													onClick={() => handleSort("winRate")}
												>
													<span className="header-text">Win Rate</span>
													<SortIcon
														column="winRate"
														currentSortColumn={sortColumn}
														sortDirection={sortDirection}
													/>
												</div>
											</th>
											<th className="spend-column">
												<div
													className={`sortable-header ${sortColumn === "spend" ? "active" : ""}`}
													onClick={() => handleSort("spend")}
												>
													<span className="header-text">Total Spend</span>
													<SortIcon
														column="spend"
														currentSortColumn={sortColumn}
														sortDirection={sortDirection}
													/>
												</div>
											</th>
											<th className="requests-column">
												<div
													className={`sortable-header ${sortColumn === "requests" ? "active" : ""}`}
													onClick={() => handleSort("requests")}
												>
													<span className="header-text">Market Requests</span>
													<SortIcon
														column="requests"
														currentSortColumn={sortColumn}
														sortDirection={sortDirection}
													/>
												</div>
											</th>
										</tr>
									</thead>
									<tbody>
										{sortedData.map((pkg, index) => {
											const packageVector = pkg.packageVector || {};
											const packageMetrics = pkg.packageMetrics || {};
											const isSelected = selectedResults.includes(
												pkg.packageId || pkg.id || index,
											);

											return (
												<tr
													key={pkg.packageId || pkg.id || index}
													className={`result-row ${isSelected ? "selected" : ""}`}
												>
													<td className="checkbox-cell">
														<input
															type="checkbox"
															checked={isSelected}
															onChange={() =>
																onResultSelect(pkg.packageId || pkg.id || index)
															}
														/>
													</td>
													<td className="name-cell">
														<div className="package-name-container">
															<span
																className="package-name"
																title={
																	packageVector.packageName ||
																	`Package ${index + 1}`
																}
															>
																{packageVector.packageName ||
																	`Package ${index + 1}`}
															</span>
															<button
																className="info-icon-btn"
																onClick={() => handleDetailsClick(pkg)}
																title="View package details"
															>
																<Info size={14} />
															</button>
															{packageVector.isCurated && (
																<span
																	className="curated-indicator"
																	title="Curated Package"
																>
																	<Star size={12} />
																</span>
															)}
														</div>
													</td>
													<td className="category-cell">
														{packageVector.packageCategoryL1 || "N/A"}
													</td>
													<td className="vertical-cell">
														{packageVector.primaryVertical || "N/A"}
													</td>
													<td className="curated-cell">
														<span
															className={`curated-badge ${packageVector.isCurated ? "yes" : "no"}`}
														>
															{packageVector.isCurated ? "Yes" : "No"}
														</span>
													</td>
													<td className="status-cell">
														<span
															className="status-badge"
															style={{
																color: getStatusColor(packageVector.dealStatus),
															}}
														>
															{packageVector.dealStatus || "Unknown"}
														</span>
													</td>
													<td className="relevance-cell">
														<div className="score-bar-container">
															<div
																className={`score-bar ${getRelevanceClass(getMatchPercentage(pkg))}`}
																style={{
																	width: `${Math.min(100, getMatchPercentage(pkg))}%`,
																}}
															></div>
															<span className="score-text">
																{getMatchPercentage(pkg)}%
															</span>
														</div>
													</td>
													<td className="score-cell">
														{pkg.compositeScore.toFixed(4)}
													</td>
													<td className="deal-price-cell">
														{formatMetric(packageVector.dealPrice, "currency")}
													</td>
													<td className="ctr-cell">
														{packageMetrics.clickThroughRate
															? formatMetric(
																	packageMetrics.clickThroughRate,
																	"percentage",
																)
															: "N/A"}
													</td>
													<td className="viewability-cell">
														{packageMetrics.viewability
															? formatMetric(
																	packageMetrics.viewability,
																	"percentage",
																)
															: "N/A"}
													</td>
													<td className="impressions-cell">
														{formatNumber(packageMetrics.impressions)}
													</td>
													<td className="fills-cell">
														{formatNumber(packageMetrics.fills)}
													</td>
													<td className="clicks-cell">
														{formatNumber(packageMetrics.cprClicks)}
													</td>
													<td className="bid-cpm-cell">
														{formatMetric(
															packageMetrics.bidCpm / 1000000,
															"currency",
														)}
													</td>
													<td className="win-rate-cell">
														{formatPercentage(packageMetrics.winRate)}
													</td>
													<td className="spend-cell">
														{formatMetric(
															packageMetrics.totalSpendInUSD / 1000000,
															"currency",
														)}
													</td>
													<td className="requests-cell">
														{formatNumber(packageMetrics.totalMarketRequests)}
													</td>
												</tr>
											);
										})}
									</tbody>
								</table>
							</div>
						)}

						{/* Action Buttons - Only show in table mode and when there are results */}
						{viewMode === "table" && (
							<div className="action-buttons">
								<button
									type="button"
									className="exclude-btn"
									disabled={
										selectedResults.length === 0 ||
										!sortedData ||
										sortedData.length === 0
									}
									onClick={() => {
										// Handle exclude selected packages
										console.log("Exclude selected packages:", selectedResults);
									}}
								>
									Exclude Selected ({selectedResults.length})
								</button>

								<PackageExportDropdown
									isExporting={isExporting}
									onExport={onExport}
								/>
							</div>
						)}

						{/* Pagination Component */}
						{(totalPages > 0 || totalItems > itemsPerPage) && (
							<Pagination
								currentPage={currentPage}
								totalPages={totalPages}
								totalItems={totalItems}
								itemsPerPage={itemsPerPage}
								onPageChange={onPageChange}
								onPageSizeChange={onPageSizeChange}
								loading={isLoading}
								showPageSizeSelector={true}
								showJumpToPage={true}
								showResultsInfo={true}
								className="package-pagination"
							/>
						)}

						{hasMore && onLoadMore && (
							<div className="load-more-section">
								<button
									className="load-more-btn"
									onClick={onLoadMore}
									disabled={isLoading}
								>
									{isLoading ? "Loading..." : "Load More Packages"}
								</button>
							</div>
						)}
					</>
				)}
			</div>

			{/* Package Details Modal */}
			<PackageDetailsModal
				isOpen={isDetailsModalOpen}
				onClose={closeDetailsModal}
				packageData={selectedPackageForDetails}
			/>

			{/* Targeting Modal */}
			<TargetingModal
				isOpen={isTargetingModalOpen}
				onClose={closeTargetingModal}
				packageData={selectedPackageForTargeting}
			/>

			{/* Create Package Modal */}
			<CreatePackageModal
				isOpen={isCreatePackageModalOpen}
				onClose={() => setIsCreatePackageModalOpen(false)}
			/>

			<style jsx>{`
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 20px;
        }

        .modal-container {
          background: white;
          border-radius: 12px;
          width: 900px;
          height: 650px;
          display: flex;
          flex-direction: column;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
          overflow: hidden;
        }

        .modal-header {
          background: #f8f9fa;
          border-bottom: 1px solid #e9ecef;
          padding: 20px 24px;
        }

        .header-content {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
        }

        .modal-title {
          margin: 0 0 6px 0;
          font-size: 20px;
          font-weight: 600;
          color: #212529;
          line-height: 1.3;
        }

        .modal-subtitle {
          margin: 0;
          font-size: 14px;
          color: #6c757d;
        }

        .close-button {
          background: #f8f9fa;
          border: 1px solid #dee2e6;
          color: #6c757d;
          font-size: 20px;
          width: 32px;
          height: 32px;
          border-radius: 6px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s ease;
        }

        .close-button:hover {
          background: #e9ecef;
          color: #495057;
        }

        .tab-navigation {
          display: flex;
          background: #f8f9fa;
          border-bottom: 1px solid #e9ecef;
        }

        .tab-button {
          flex: 1;
          padding: 14px 16px;
          border: none;
          background: transparent;
          color: #6c757d;
          font-weight: 500;
          font-size: 14px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          transition: all 0.2s ease;
          border-bottom: 2px solid transparent;
          position: relative;
        }

        .tab-button:hover {
          background: #f0f0f0;
          color: #495057;
        }

        .tab-button.active {
          background: white;
          color: #495057;
          border-bottom-color: #495057;
          font-weight: 600;
        }

        .tab-icon {
          font-size: 16px;
        }

        .modal-body {
          flex: 1;
        }

        /* Tab Content Styles */
        .tab-content {
          padding: 24px;
          background: #f9fafb;
          overflow-y: auto;
          height: calc(100vh - 200px);
          max-height: 600px;
        }

        /* Overview Content Styles */
        .overview-content {
          display: flex;
          flex-direction: column;
          gap: 24px;
        }

        .kpi-section {
          background: white;
          border-radius: 8px;
          padding: 24px;
          border: 1px solid #e5e7eb;
        }

        .section-title {
          font-size: 16px;
          font-weight: 600;
          color: #374151;
          margin: 0 0 20px 0;
        }

        .kpi-grid {
          display: grid;
          grid-template-columns: repeat(5, 1fr);
          gap: 16px;
          margin-bottom: 24px;
        }

        .kpi-card {
          background: #f8f9fa;
          border: 1px solid #e9ecef;
          border-radius: 8px;
          padding: 16px;
          text-align: center;
        }

        .kpi-value {
          font-size: 20px;
          font-weight: 700;
          color: #1f2937;
          margin-bottom: 8px;
          line-height: 1.2;
        }

        .kpi-label {
          font-size: 11px;
          font-weight: 500;
          color: #6b7280;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .composite-scores {
          display: flex;
          gap: 12px;
          justify-content: center;
        }

        .score-badge {
          padding: 6px 16px;
          border-radius: 16px;
          font-size: 13px;
          font-weight: 500;
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }

        .score-badge.composite {
          background: #dbeafe;
          color: #1d4ed8;
        }

        .score-badge.search {
          background: #e0f2fe;
          color: #0277bd;
        }

        /* Metrics Section */
        .metrics-section {
          background: white;
          border-radius: 8px;
          padding: 24px;
          border: 1px solid #e5e7eb;
        }

        .metrics-table {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 32px;
        }

        .metrics-column {
          min-width: 0;
        }

        .column-header {
          font-size: 12px;
          font-weight: 600;
          color: #6b7280;
          margin: 0 0 16px 0;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .metric-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 10px 0;
          border-bottom: 1px solid #f3f4f6;
          font-size: 13px;
        }

        .metric-row:last-child {
          border-bottom: none;
        }

        .metric-name {
          color: #4b5563;
          font-weight: 400;
        }

        .metric-val {
          color: #1f2937;
          font-weight: 600;
        }

        /* Device Section */
        .device-section {
          background: white;
          border-radius: 8px;
          padding: 24px;
          border: 1px solid #e5e7eb;
        }

        .device-placeholder {
          text-align: center;
          padding: 32px;
          color: #6b7280;
          border: 2px dashed #e5e7eb;
          border-radius: 8px;
          background: #f9fafb;
        }

        .device-placeholder p {
          margin: 8px 0;
          font-size: 14px;
        }

        /* Technical Content Styles */
        .technical-content {
          display: flex;
          flex-direction: column;
          gap: 24px;
        }

        .technical-metrics-section {
          background: white;
          border-radius: 8px;
          padding: 24px;
          border: 1px solid #e5e7eb;
        }

        .technical-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 24px;
        }

        .technical-column {
          min-width: 0;
        }

        .technical-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 10px 0;
          border-bottom: 1px solid #f3f4f6;
          font-size: 13px;
        }

        .technical-item:last-child {
          border-bottom: none;
        }

        .technical-label {
          color: #4b5563;
          font-weight: 400;
        }

        .technical-value {
          color: #1f2937;
          font-weight: 600;
        }

        .technical-details-section {
          background: white;
          border-radius: 8px;
          padding: 24px;
          border: 1px solid #e5e7eb;
        }

        /* Deals Content Styles */
        .deals-content {
          display: flex;
          flex-direction: column;
          gap: 24px;
        }

        .basic-info-section,
        .category-section {
          background: white;
          border-radius: 8px;
          padding: 24px;
          border: 1px solid #e5e7eb;
        }

        .info-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 20px 32px;
        }

        .info-item {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        .info-label {
          font-size: 11px;
          font-weight: 600;
          color: #6b7280;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .info-value {
          font-size: 13px;
          color: #1f2937;
          font-weight: 500;
          word-break: break-word;
        }

        /* OXTL Section */
        .oxtl-section {
          background: white;
          border-radius: 8px;
          padding: 24px;
          border: 1px solid #e5e7eb;
        }

        .oxtl-content {
          margin-top: 12px;
        }

        .oxtl-text {
          font-family: 'Courier New', monospace;
          font-size: 12px;
          line-height: 1.6;
          background: #f8f9fa;
          padding: 16px;
          border-radius: 6px;
          border: 1px solid #e9ecef;
          word-break: break-all;
          white-space: pre-wrap;
          color: #495057;
        }

        /* Main Layout Styles */
        .results-container {
          background: white;
          border-radius: 8px;
          overflow: hidden;
        }

        .results-section {
          overflow: visible;
          position: relative;
          z-index: 1;
        }

        .results-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px;
          border-bottom: 1px solid #e5e7eb;
          background: #f9fafb;
        }

        .section-title {
          display: flex;
          align-items: center;
          gap: 10px;
          margin: 0;
          font-size: 18px;
          font-weight: 600;
          color: #212529;
        }

        .sort-status {
          font-size: 14px;
          color: #0076BD;
          font-weight: 500;
        }

        .clear-sort-btn {
          font-size: 12px;
          color: #6c757d;
          background: none;
          border: 1px solid #dee2e6;
          border-radius: 4px;
          padding: 4px 8px;
          cursor: pointer;
          transition: all 0.2s ease;
          margin-left: 8px;
          font-weight: normal;
        }

        .clear-sort-btn:hover {
          color: #495057;
          border-color: #adb5bd;
          background-color: #f8f9fa;
        }

        .results-actions {
          display: flex;
          gap: 12px;
          align-items: center;
        }

        .view-toggle-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 12px;
          border: 1px solid #dee2e6;
          border-radius: 6px;
          background: white;
          color: #495057;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }

        .view-toggle-btn:hover {
          background: #f8f9fa;
          border-color: #0076BD;
          color: #0076BD;
        }

        .create-package-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 16px;
          border: 1px solid #0076BD;
          border-radius: 6px;
          background: #0076BD;
          color: white;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }

        .create-package-btn:hover {
          background: #005a94;
          border-color: #005a94;
        }

        /* Curation Filter Styles */
        .curation-filter {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 16px 20px;
          background: #f8f9fa;
          border-bottom: 1px solid #e5e7eb;
        }

        .filter-buttons {
          display: flex;
          gap: 8px;
        }

        .filter-button {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 8px 12px;
          border: 1px solid #dee2e6;
          border-radius: 6px;
          background: white;
          color: #495057;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }

        .filter-button:hover {
          background: #f8f9fa;
          border-color: #0076BD;
          color: #0076BD;
        }

        .filter-button.active {
          background: #0076BD;
          border-color: #0076BD;
          color: white;
        }

        .filter-count {
          font-size: 12px;
          opacity: 0.8;
        }

        /* Card View Styles */
        .results-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 20px;
          padding: 20px;
          background: #f8f9fa;
          max-width: 100%;
          overflow: hidden;
        }

        .package-card {
          background: white;
          border-radius: 8px;
          border: 1px solid #e5e7eb;
          position: relative;
          transition: all 0.2s;
          height: 480px;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          min-width: 0;
        }

        .package-card:hover {
          border-color: #0076BD;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }

        .package-card.selected {
          border-color: #0076BD;
          box-shadow: 0 0 0 2px rgba(0, 118, 189, 0.2);
        }

        .match-badge {
          position: absolute;
          top: 12px;
          right: 12px;
          padding: 4px 8px;
          border-radius: 12px;
          font-size: 11px;
          font-weight: 600;
          background: #e3f2fd;
          color: #1976d2;
          z-index: 2;
        }

        .curation-badge {
          position: absolute;
          top: 12px;
          left: 12px;
          display: flex;
          align-items: center;
          gap: 4px;
          padding: 4px 8px;
          border-radius: 12px;
          font-size: 10px;
          font-weight: 600;
          background: #fff3cd;
          color: #856404;
          z-index: 2;
        }

        .card-content {
          padding: 16px;
          flex: 1;
          display: flex;
          flex-direction: column;
        }

        .card-header {
          margin-bottom: 12px;
        }

        .package-title {
          margin: 0 0 4px 0;
          font-size: 16px;
          font-weight: 600;
          color: #333;
          line-height: 1.3;
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
          text-overflow: ellipsis;
          max-height: calc(1.3em * 3);
          word-wrap: break-word;
          cursor: help;
        }

        .package-title:hover {
          color: #0076BD;
        }

        .package-subtitle {
          font-size: 12px;
          color: #6c757d;
        }

        .metrics-row {
          display: flex;
          gap: 1px;
          margin-bottom: 12px;
        }

        .metric {
          flex: 1;
          text-align: center;
          padding: 12px 8px;
          background: #f8f9fa;
        }

        .metric:first-child {
          border-radius: 4px 0 0 4px;
        }

        .metric:last-child {
          border-radius: 0 4px 4px 0;
        }

        .metric-label {
          font-size: 10px;
          color: #6c757d;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 4px;
        }

        .metric-value {
          font-size: 14px;
          color: #333;
          font-weight: 600;
        }

        .metric-value-device {
          font-size: 13px;
          color: #333;
          font-weight: 400;
        }

        .card-actions {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 12px;
          margin-top: 20px;
        }

        .package-checkbox {
          width: 16px;
          height: 16px;
          cursor: pointer;
        }

        .btn-details, .btn-targeting {
          display: flex;
          align-items: center;
          gap: 4px;
          padding: 6px 12px;
          border: 1px solid #dee2e6;
          border-radius: 4px;
          background: white;
          color: #495057;
          font-size: 11px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }

        .btn-details:hover, .btn-targeting:hover {
          background: #f8f9fa;
          border-color: #0076BD;
          color: #0076BD;
        }

        .days-remaining {
          margin-left: auto;
          font-size: 11px;
          color: #6c757d;
        }

        .status-indicator {
          display: flex;
          justify-content: flex-end;
        }

        .status-badge {
          padding: 4px 8px;
          border-radius: 12px;
          font-size: 10px;
          font-weight: 600;
          color: white;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        /* Table Styles */
        .results-table-container {
          overflow-x: auto;
          overflow-y: visible;
          position: static;
          border: 1px solid #dee2e6;
          border-radius: 8px;
          background-color: white;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
          margin: 20px;
        }

        .results-table {
          width: 100%;
          border-collapse: collapse;
          table-layout: fixed;
          font-size: 14px;
          min-width: 1900px;
        }

        .results-table .checkbox-column { width: 40px; }
        .results-table .name-column { width: 200px; }
        .results-table .category-column { width: 150px; }
        .results-table .vertical-column { width: 150px; }
        .results-table .curated-column { width: 80px; }
        .results-table .status-column { width: 80px; }
        .results-table .relevance-column { width: 100px; }
        .results-table .score-column { width: 70px; }
        .results-table .deal-price-column { width: 90px; }
        .results-table .ctr-column { width: 70px; }
        .results-table .viewability-column { width: 80px; }
        .results-table .impressions-column { width: 130px; }
        .results-table .fills-column { width: 120px; }
        .results-table .clicks-column { width: 100px; }
        .results-table .bid-cpm-column { width: 90px; }
        .results-table .win-rate-column { width: 80px; }
        .results-table .spend-column { width: 100px; }
        .results-table .requests-column { width: 130px; }

        .results-table th {
          background-color: #f8f9fa;
          border-bottom: 2px solid #dee2e6;
          padding: 12px 8px;
          text-align: left;
          font-weight: 600;
          font-size: 12px;
          color: #495057;
          white-space: nowrap;
          position: static;
          overflow: hidden;
        }

        .results-table td {
          padding: 12px 8px;
          border-bottom: 1px solid #e9ecef;
          vertical-align: middle;
          text-overflow: ellipsis;
          white-space: nowrap;
          position: static;
          overflow: hidden;
        }

        .checkbox-cell, .checkbox-column {
          text-align: center;
          padding: 8px;
        }

        .curated-cell, .relevance-cell, .deal-price-cell, .ctr-cell, .viewability-cell, .impressions-cell,
        .fills-cell, .clicks-cell, .bid-cpm-cell, .win-rate-cell, .spend-cell, .requests-cell {
          text-align: center;
        }

        .package-name-container {
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .info-note {
  background: #e3f2fd;
  border-left: 4px solid #2196f3;
  padding: 12px 16px;
  margin-bottom: 20px;
  border-radius: 4px;
  font-size: 13px;
  color: #1565c0;
  display: flex;
  align-items: center;
  gap: 8px;
}

        .info-icon-btn {
          background: none;
          border: none;
          cursor: pointer;
          color: #6c757d;
          padding: 2px;
          border-radius: 3px;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .info-icon-btn:hover {
          background-color: #f8f9fa;
          color: #0076BD;
        }

        .curated-indicator {
          color: #f39c12;
          flex-shrink: 0;
        }

        .curated-badge {
          padding: 2px 6px;
          border-radius: 12px;
          font-size: 10px;
          font-weight: 600;
          text-transform: uppercase;
        }

        .curated-badge.yes {
          background: #dcfce7;
          color: #166534;
        }

        .curated-badge.no {
          background: #f1f5f9;
          color: #475569;
        }

        /* Score Bar Styling */
        .score-bar-container {
          position: relative;
          width: 100%;
          height: 20px;
          background-color: #f8f9fa;
          border-radius: 10px;
          overflow: hidden;
          display: flex;
          align-items: center;
        }

        .score-bar {
          height: 100%;
          transition: width 0.3s ease;
          border-radius: 10px;
        }

        .score-bar.excellent { 
          background-color: #28a745;
        }
        .score-bar.great { 
          background-color: #17a2b8;
        }
        .score-bar.acceptable { 
          background-color: #ffc107;
        }
        .score-bar.poor { 
          background-color: #fd7e14;
        }
        .score-bar.irrelevant { 
          background-color: #6c757d;
        }

        .score-text {
          position: absolute;
          left: 50%;
          top: 50%;
          transform: translate(-50%, -50%);
          font-size: 11px;
          font-weight: 600;
          color: #212529;
          text-shadow: 0 1px 2px rgba(255,255,255,0.8);
          z-index: 1;
        }

        .package-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          width: 100%;
          min-width: 0;
        }

        .sortable-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          cursor: pointer;
          user-select: none;
          transition: all 0.2s;
          padding: 2px 4px;
          border-radius: 3px;
          flex: 1;
          min-width: 0;
        }

        .sortable-header:hover {
          background-color: rgba(0, 118, 189, 0.1);
        }

        .sortable-header.active {
          background-color: rgba(0, 118, 189, 0.15);
          color: #0076BD;
          font-weight: 600;
        }

        .sortable-header .header-text {
          flex: 1;
          min-width: 0;
        }

        .sort-icon {
          display: inline-flex;
          align-items: center;
          margin-left: 4px;
          opacity: 0.6;
          transition: opacity 0.2s;
          flex-shrink: 0;
        }

        .sortable-header:hover .sort-icon,
        .sortable-header.active .sort-icon {
          opacity: 1;
        }

        .search-toggle-btn {
          background: none;
          border: none;
          cursor: pointer;
          font-size: 12px;
          padding: 4px;
          border-radius: 3px;
          transition: background-color 0.2s;
          flex-shrink: 0;
          margin-left: 4px;
        }

        .search-toggle-btn:hover {
          background-color: rgba(0,0,0,0.1);
        }

        .search-header-container {
          position: relative;
          display: flex;
          align-items: center;
          width: 100%;
        }

        .package-search-input {
          width: 100%;
          padding: 3px 20px 3px 6px;
          border: 1px solid #d1d5db;
          border-radius: 3px;
          font-size: 11px;
          background-color: white;
          transition: border-color 0.2s;
          height: 22px;
          line-height: 1;
        }

        .package-search-input:focus {
          outline: none;
          border-color: #0076BD;
          box-shadow: 0 0 0 2px rgba(0, 118, 189, 0.1);
        }

        .close-search-btn {
          position: absolute;
          right: 3px;
          top: 50%;
          transform: translateY(-50%);
          background: none;
          border: none;
          color: #6b7280;
          cursor: pointer;
          font-size: 14px;
          line-height: 1;
          padding: 1px 2px;
          border-radius: 2px;
          transition: color 0.2s;
        }

        .close-search-btn:hover {
          color: #374151;
          background-color: #f3f4f6;
        }

        .result-row {
          transition: background-color 0.2s ease;
        }

        .result-row:hover {
          background-color: #f8f9fa;
        }

        .result-row.selected {
          background-color: #e3f2fd;
        }

        .package-name {
          font-weight: 500;
          color: #212529;
          cursor: help;
          max-width: 180px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          display: inline-block;
        }

        .package-name:hover {
          color: #0076BD;
        }

        /* States */
        .loading-state, .empty-state, .error-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 60px 20px;
          text-align: center;
        }

        .loading-spinner {
          width: 40px;
          height: 40px;
          border: 4px solid #f3f3f3;
          border-top: 4px solid #0076BD;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin-bottom: 20px;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .empty-icon, .error-icon {
          color: #9ca3af;
          margin-bottom: 20px;
        }

        .empty-state h3, .error-state h3 {
          margin: 0 0 15px 0;
          color: #333;
        }

        .empty-state p, .error-state p {
          margin: 0 0 15px 0;
          color: #666;
          line-height: 1.5;
          max-width: 400px;
        }

        .retry-button {
          padding: 10px 20px;
          background-color: #0076BD;
          color: white;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-weight: 500;
          transition: background-color 0.2s;
        }

        .retry-button:hover {
          background-color: #005a94;
        }

        .load-more-section {
          text-align: center;
          padding: 20px;
        }

        .load-more-btn {
          padding: 12px 24px;
          background: #0076BD;
          color: white;
          border: none;
          border-radius: 6px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: background-color 0.2s;
        }

        .load-more-btn:hover:not(:disabled) {
          background: #005a94;
        }

        .load-more-btn:disabled {
          background: #d1d5db;
          color: #9ca3af;
          cursor: not-allowed;
        }

        /* Action Buttons */
        .action-buttons {
          display: flex;
          gap: 12px;
          align-items: center;
          margin-top: 20px;
          padding: 16px;
          background-color: #f8f9fa;
          border-radius: 8px;
          border: 1px solid #dee2e6;
          margin: 20px;
        }

        .btn-medium-secondary {
          padding: 8px 16px;
          background-color: #1FBAB0;
          color: white;
          border: none;
          border-radius: 4px;
          font-weight: 500;
          cursor: pointer;
          transition: background-color 0.2s;
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .btn-medium-secondary:hover:not(:disabled) {
          background-color: #199f98;
        }

        .btn-medium-secondary:disabled {
          background-color: rgb(24, 176, 179);
          cursor: not-allowed;
        }

        .exclude-btn {
          padding: 8px 16px;
          background-color: #cb2816ff;
          color: white;
          border: none;
          border-radius: 4px;
          font-weight: 500;
          cursor: pointer;
          transition: background-color 0.2s;
        }

        .exclude-btn:hover:not(:disabled) {
          background-color: #f2a09cff;
        }

        .exclude-btn:disabled {
          background-color: #f2a09cff;
          cursor: not-allowed;
        }

.deal-ids-section {
  background: white;
  border-radius: 8px;
  padding: 24px;
  border: 1px solid #e5e7eb;
  margin-bottom: 24px;
}

.deal-ids-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
  margin-top: 16px;
}

.deal-id-item {
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 14px 16px;
  transition: all 0.2s ease;
}

.deal-id-item:hover {
  background: #e9ecef;
  border-color: #0076BD;
  box-shadow: 0 2px 8px rgba(0, 118, 189, 0.1);
}

.deal-id-label {
  font-size: 11px;
  font-weight: 600;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
}

.deal-id-value {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.deal-id-code {
  font-family: 'Courier New', monospace;
  font-size: 14px;
  font-weight: 600;
  color: #212529;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
}

.copy-btn {
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  padding: 6px 8px;
  transition: all 0.2s;
  flex-shrink: 0;
  line-height: 1;
}

.copy-btn:hover {
  background: #0076BD;
  border-color: #0076BD;
  transform: scale(1.1);
}

.info-note {
  font-size: 13px;
  color: #6c757d;
  font-style: italic;
  margin: -8px 0 16px 0;
  padding: 10px;
  background: #f8f9fa;
  border-left: 3px solid #ffc107;
  border-radius: 4px;
}

.span-full {
  grid-column: 1 / -1;
}

.aggregation-note {
  background: #e3f2fd;
  padding: 10px 14px;
  border-radius: 6px;
  color: #1565c0;
  font-weight: 500;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 8px;
}

        /* Responsive Design */
        @media (max-width: 1200px) {
          .results-grid {
            grid-template-columns: repeat(2, 1fr);
          }

          .results-table {
            min-width: 1700px;
            font-size: 13px;
          }

          .curation-filter {
            flex-direction: column;
            align-items: flex-start;
            gap: 8px;
          }

          .filter-buttons {
            flex-wrap: wrap;
          }

          .kpi-grid {
            grid-template-columns: repeat(3, 1fr);
          }
          
          .metrics-table {
            grid-template-columns: 1fr;
            gap: 24px;
          }
          
          .technical-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        @media (max-width: 768px) {
          .results-header {
            flex-direction: column;
            gap: 12px;
            align-items: flex-start;
          }

          .results-actions {
            width: 100%;
            justify-content: flex-end;
          }

          .results-grid {
            grid-template-columns: 1fr;
            padding: 12px;
          }

          .card-actions {
            flex-wrap: wrap;
          }

          .days-remaining {
            margin-left: 0;
            width: 100%;
            text-align: center;
            margin-top: 8px;
          }

          .results-table {
            min-width: 1500px;
            font-size: 12px;
          }

          .action-buttons {
            flex-direction: column;
            align-items: stretch;
            gap: 8px;
          }

          .modal-container {
            margin: 10px;
            max-height: 95vh;
          }

          .tab-navigation {
            flex-wrap: wrap;
          }

          .tab-button {
            padding: 10px 16px;
            font-size: 13px;
          }

          .curation-filter {
            padding: 12px 16px;
          }

          .filter-buttons {
            width: 100%;
          }

          .filter-button {
            flex: 1;
            justify-content: center;
            padding: 6px 8px;
            font-size: 12px;
          }

          .kpi-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: 12px;
          }

          .kpi-card {
            padding: 12px;
          }

          .kpi-value {
            font-size: 18px;
          }

          .info-grid {
            grid-template-columns: 1fr;
            gap: 16px;
          }

          .technical-grid {
            grid-template-columns: 1fr;
            gap: 16px;
          }

          .metrics-section,
          .basic-info-section,
          .technical-metrics-section {
            padding: 20px;
          }

          .composite-scores {
            flex-direction: column;
            align-items: center;
          }
        }
      `}</style>
		</div>
	);
};
